import { CSSTransition } from 'react-transition-group';
import { useState, useRef, useEffect } from 'react';
import HelpInfo from '@Shared/Components/HelpInfo';
import SvgIcon from '@Shared/Ui/SvgIcon';
import Image from '@Shared/Ui/Image';
import PropTypes from 'prop-types';
function Collapse({
    title,
    children,
    helpText,
    success,
    size = 'medium',
    className = '',
    warning,
    description = '',
    iconId,
    iconCollectionId,
    isActive = false,
    cb,
    badge = null,
    blurContent,
    wrapperContent,
    outline = false,
    disabled = false,
    isDemo,
}) {
    const contentRef = useRef(null);

    const [isOpen, setIsOpen] = useState(isActive);

    useEffect(() => {
        setIsOpen(isActive);
    }, [isActive]);

    useEffect(() => {
        if (isActive !== isOpen && cb && typeof cb === 'function') {
            cb(isOpen);
        }
    }, [isOpen]);

    const toggleAccordion = () => {
        setIsOpen(!isOpen);
    };

    const getSize = () => {
        if (size === 'medium') {
            return '';
        } else if (size === 'large') {
            return 'h-16';
        }
    };

    return (
        <div
            className={`${className} bg-surface-light ${success ? `border-primary-blue border border-solid ${isOpen ? 'bg-surface-light' : '!bg-preview-body'}` : ''} ${warning ? `border-primary-yellow border border-solid ${isOpen ? 'bg-surface-light' : '!bg-surface-yellow'}` : ''} ${outline ? 'border-outline-main border border-solid' : ''} ${disabled ? 'pointer-events-none opacity-40' : ''} rounded-lg`}
        >
            <div
                onClick={toggleAccordion}
                className={`${getSize()} text-text-dark relative flex min-h-14 w-full cursor-pointer px-6 py-2 text-left focus:outline-none`}
            >
                <div className={'flex items-center gap-2'}>
                    {success ? (
                        <SvgIcon
                            collectionName={'builder-icons-sprite'}
                            iconId={'collapse-success'}
                        />
                    ) : null}
                    {warning ? (
                        <SvgIcon collectionName={'builder-icons-sprite'} iconId={'warning'} />
                    ) : null}
                    {iconId && iconCollectionId ? (
                        <SvgIcon collectionName={iconCollectionId} iconId={iconId} />
                    ) : null}
                    <div className={'flex flex-col justify-center'}>
                        <div className='flex items-center gap-2'>
                            <div className={'flex items-center gap-2'}>
                                <p className='font-bold'>{title}</p>
                                {isDemo && helpText ? (
                                    <div
                                        onClick={(e) => e.stopPropagation()}
                                        className='group relative'
                                    >
                                        <p
                                            className={`bg-surface-light absolute z-30 hidden max-h-[400px] w-[200px] translate-x-8 transform rounded-md p-10 shadow-xl group-hover:flex`}
                                        >
                                            {helpText}
                                        </p>
                                        <Image icon={'info'} />
                                    </div>
                                ) : helpText ? (
                                    <HelpInfo info={helpText} />
                                ) : null}

                                {badge}
                            </div>
                        </div>
                        {description && (
                            <div className={'text-text-secondary font-base mt-0.5 text-xs'}>
                                {description}
                            </div>
                        )}
                    </div>
                </div>
                <Image
                    className={`text-text-dark duration-400 absolute right-2 top-1/2 size-6 -translate-y-1/2 transition-transform ease-linear ${isOpen ? 'rotate-180' : 'rotate-0'}`}
                    icon='arrow-down'
                />
            </div>
            <CSSTransition
                nodeRef={contentRef}
                in={isOpen}
                timeout={400}
                classNames='accordion'
                unmountOnExit
            >
                <div className={'relative'}>
                    {wrapperContent ? (
                        <div
                            className={
                                'absolute bottom-0 left-0 right-0 top-0 z-10 flex items-center justify-center'
                            }
                        >
                            {wrapperContent}
                        </div>
                    ) : null}
                    <div ref={contentRef} className={`p-6 pt-0 ${blurContent ? 'blur' : ''}`}>
                        {children}
                    </div>
                </div>
            </CSSTransition>
        </div>
    );
}

Collapse.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    helpText: PropTypes.string,
    success: PropTypes.oneOfType([PropTypes.bool, PropTypes.array, PropTypes.string]),
    size: PropTypes.oneOf(['medium', 'large']),
};

export default Collapse;
